import { logEvent } from 'firebase/analytics';

import { isAnalyticsEnabled } from './analyticsConfig';
import { journeySessionService } from './journeySessionService';
import { analytics, auth } from '../firebase/client';

export type ShippingQuoteSource =
  | 'subscription_review'
  | 'shipping_page'
  | 'beer_store_shipping';

export interface ShippingQuoteEventParams {
  source: ShippingQuoteSource;
  cepPrefix?: string;
  shippingPrice?: number;
  subscriptionPlan?: string;
  errorMessage?: string;
}

export interface ShippingAdvanceEventParams {
  source: ShippingQuoteSource;
  isAuthenticated: boolean;
  hadShippingQuote: boolean;
  shippingPrice?: number;
  subscriptionPlan?: string;
  redirectTo?: string;
}

export interface ShippingAbandonEventParams {
  source: ShippingQuoteSource;
  shippingPrice: number;
  timeOnPageSeconds: number;
  subscriptionPlan?: string;
}

// Tipos para os eventos de analytics
export interface BeerStoreAccessEvent {
  page: string;
  timestamp: string;
  user_id?: string;
}

export interface BeerDetailViewEvent {
  beer_id: string;
  beer_name: string;
  beer_price: number;
  beer_category?: string;
  timestamp: string;
  user_id?: string;
}

export interface SubscriptionFlowEvent {
  step:
    | 'personal_form'
    | 'account'
    | 'account_created'
    | 'address'
    | 'address_saved'
    | 'shipping_options'
    | 'payment'
    | 'review'
    | 'success'
    | 'error';
  subscription_plan?: string;
  subscription_value?: number;
  timestamp: string;
  user_id?: string;
}

export interface PurchaseEvent {
  transaction_id: string;
  value: number;
  currency: string;
  items: Array<{
    item_id: string;
    item_name: string;
    category: string;
    quantity: number;
    price: number;
  }>;
  timestamp: string;
  user_id?: string;
}

class AnalyticsService {
  private shouldTrack(): boolean {
    return isAnalyticsEnabled();
  }

  private logEventSafe(eventName: string, params: object): void {
    if (!this.shouldTrack() || !analytics) {
      return;
    }

    logEvent(analytics, eventName, params as Record<string, unknown>);
  }

  private getUserId(): string | undefined {
    return auth.currentUser?.uid;
  }

  getSessionId(): string {
    return journeySessionService.getSessionId();
  }

  private getTimestamp(): string {
    return new Date().toISOString();
  }

  private getCepPrefix(cep?: string): string | undefined {
    const digits = cep?.replace(/\D/g, '') ?? '';
    return digits.length >= 3 ? digits.slice(0, 3) : undefined;
  }

  private baseEventParams() {
    return {
      timestamp: this.getTimestamp(),
      user_id: this.getUserId(),
      session_id: this.getSessionId(),
    };
  }

  private currentPath(): string {
    return window.location.pathname;
  }

  /**
   * Track CTA clicks (also persisted to journey_sessions in Firestore).
   */
  trackCta(
    ctaId: string,
    label?: string,
    meta?: Record<string, string | number | boolean | null>,
  ) {
    if (!this.shouldTrack()) {
      return;
    }

    const path = this.currentPath();
    journeySessionService.trackCta(ctaId, path, label, meta);
    this.logEventSafe('cta_click', {
      ...this.baseEventParams(),
      cta_id: ctaId,
      cta_label: label,
      page_path: path,
    });
  }

  trackShippingQuoteRequested(params: ShippingQuoteEventParams) {
    if (!this.shouldTrack()) {
      return;
    }

    this.logEventSafe('shipping_quote_requested', {
      ...this.baseEventParams(),
      source: params.source,
      cep_prefix: this.getCepPrefix(params.cepPrefix),
      subscription_plan: params.subscriptionPlan,
    });
  }

  trackShippingQuoteSuccess(params: ShippingQuoteEventParams) {
    if (!this.shouldTrack()) {
      return;
    }

    this.logEventSafe('shipping_quote_success', {
      ...this.baseEventParams(),
      source: params.source,
      cep_prefix: this.getCepPrefix(params.cepPrefix),
      shipping_price: params.shippingPrice,
      subscription_plan: params.subscriptionPlan,
    });
  }

  trackShippingQuoteError(params: ShippingQuoteEventParams) {
    if (!this.shouldTrack()) {
      return;
    }

    this.logEventSafe('shipping_quote_error', {
      ...this.baseEventParams(),
      source: params.source,
      cep_prefix: this.getCepPrefix(params.cepPrefix),
      subscription_plan: params.subscriptionPlan,
      error_message: params.errorMessage?.slice(0, 100),
    });
  }

  trackShippingAdvanceClick(params: ShippingAdvanceEventParams) {
    if (!this.shouldTrack()) {
      return;
    }

    this.logEventSafe('shipping_advance_click', {
      ...this.baseEventParams(),
      source: params.source,
      is_authenticated: params.isAuthenticated,
      had_shipping_quote: params.hadShippingQuote,
      shipping_price: params.shippingPrice,
      subscription_plan: params.subscriptionPlan,
      redirect_to: params.redirectTo,
    });
  }

  trackShippingAbandon(params: ShippingAbandonEventParams) {
    if (!this.shouldTrack()) {
      return;
    }

    this.logEventSafe('shipping_abandon', {
      ...this.baseEventParams(),
      source: params.source,
      shipping_price: params.shippingPrice,
      time_on_page_seconds: params.timeOnPageSeconds,
      subscription_plan: params.subscriptionPlan,
    });
  }

  /**
   * Track quando o usuário acessa a beer-store
   */
  trackBeerStoreAccess(page: string = 'beer-store') {
    if (!this.shouldTrack()) {
      return;
    }

    const event: BeerStoreAccessEvent = {
      page,
      timestamp: this.getTimestamp(),
      user_id: this.getUserId(),
    };

    this.logEventSafe('beer_store_access', event);
    // console.log('Analytics: Beer Store Access', event);
  }

  /**
   * Track quando o usuário visualiza detalhes de uma cerveja
   */
  trackBeerDetailView(
    beerId: string,
    beerName: string,
    beerPrice: number,
    beerCategory?: string,
  ) {
    if (!this.shouldTrack()) {
      return;
    }

    const event: BeerDetailViewEvent = {
      beer_id: beerId,
      beer_name: beerName,
      beer_price: beerPrice,
      beer_category: beerCategory,
      timestamp: this.getTimestamp(),
      user_id: this.getUserId(),
    };

    this.logEventSafe('beer_detail_view', event);
    // console.log('Analytics: Beer Detail View', event);
  }

  /**
   * Track progresso no fluxo de subscription
   */
  trackSubscriptionFlow(
    step: SubscriptionFlowEvent['step'],
    subscriptionPlan?: string,
    subscriptionValue?: number,
  ) {
    if (!this.shouldTrack()) {
      return;
    }

    const event: SubscriptionFlowEvent = {
      step,
      subscription_plan: subscriptionPlan,
      subscription_value: subscriptionValue,
      timestamp: this.getTimestamp(),
      user_id: this.getUserId(),
    };

    journeySessionService.trackFunnelStep(step, this.currentPath(), {
      subscription_plan: subscriptionPlan ?? null,
      subscription_value: subscriptionValue ?? null,
    });

    if (step === 'success') {
      journeySessionService.markCompleted();
    }

    this.logEventSafe('subscription_flow', {
      ...event,
      session_id: this.getSessionId(),
    });
  }

  /**
   * Track compra/pagamento
   */
  trackPurchase(
    transactionId: string,
    value: number,
    currency: string = 'BRL',
    items: PurchaseEvent['items'],
  ) {
    if (!this.shouldTrack()) {
      return;
    }

    const event: PurchaseEvent = {
      transaction_id: transactionId,
      value,
      currency,
      items,
      timestamp: this.getTimestamp(),
      user_id: this.getUserId(),
    };

    this.logEventSafe('purchase', event);
    // console.log('Analytics: Purchase', event);
  }

  /**
   * Track eventos de carrinho
   */
  trackAddToCart(
    beerId: string,
    beerName: string,
    price: number,
    quantity: number = 1,
  ) {
    if (!this.shouldTrack()) {
      return;
    }

    this.logEventSafe('add_to_cart', {
      currency: 'BRL',
      value: price * quantity,
      items: [
        {
          item_id: beerId,
          item_name: beerName,
          category: 'beer',
          quantity,
          price,
        },
      ],
      timestamp: this.getTimestamp(),
      user_id: this.getUserId(),
    });
    // console.log('Analytics: Add to Cart', {
    //   beerId,
    //   beerName,
    //   price,
    //   quantity,
    // });
  }

  /**
   * Track eventos de página
   */
  trackPageView(pageName: string, pageTitle?: string) {
    if (!this.shouldTrack()) {
      return;
    }

    const path = pageName.startsWith('/')
      ? pageName
      : window.location.pathname;

    journeySessionService.trackPageView(path, pageTitle || pageName);

    this.logEventSafe('page_view', {
      ...this.baseEventParams(),
      page_title: pageTitle || pageName,
      page_location: window.location.href,
      page_path: path,
    });
  }
}

// Exportar instância singleton
export const analyticsService = new AnalyticsService();
export default analyticsService;
